import React, { useContext } from 'react';
import styled from 'styled-components';
import { motion } from 'framer-motion';
import { ThemeContext } from '../ThemeContext';

const Container = styled.div`
  padding: 10px 10px 10px 20px;
`;

const Title = styled.h2`
  margin-bottom: 28px;
  color: ${props => props.theme === 'light' ? '#333' : '#e0e0e0'};

  .highlight {
    color: var(--highlight-color-${props => props.theme === 'light' ? 'light' : 'dark'});
  }
`;

const EntryList = styled.div`
  display: flex;
  flex-direction: column;
  gap: 20px;
`;

const Entry = styled.div`
  position: relative;
  padding: 16px 16px 16px 20px;
  border-radius: 8px;
  background-color: ${props => props.theme === 'light' ? '#fafafa' : '#1a1a1a'};
  border: 1px solid ${props => props.theme === 'light' ? '#e8e8e8' : '#2a2a2a'};
  transition: transform 0.25s ease, box-shadow 0.25s ease;

  &:hover {
    transform: translateY(-2px);
    box-shadow: 0 4px 16px rgba(0, 0, 0, ${props => props.theme === 'light' ? '0.08' : '0.3'});
  }
`;

const EntryHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  flex-wrap: wrap;
  gap: 10px;
`;

const Position = styled.h4`
  color: ${props => props.theme === 'light' ? '#333' : '#d0d0d0'};
  font-weight: 600;
  margin: 0;
  font-size: 1.1rem;
`;

const Company = styled.h4`
  color: ${props => props.theme === 'light' ? '#666' : '#a0a0a0'};
  margin: 2px 0 0 0;
  font-size: 0.95rem;
  font-weight: 400;
`;

const CurrentBadge = styled.span`
  display: inline-block;
  margin-left: 8px;
  padding: 1px 8px;
  font-size: 0.7rem;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.5px;
  border-radius: 10px;
  vertical-align: middle;
  background-color: ${props => props.theme === 'light' ? 'var(--highlight-color-light)' : 'var(--highlight-color-dark)'};
  color: ${props => props.theme === 'light' ? '#fff' : '#1a1a1a'};
`;

const Period = styled.span`
  color: ${props => props.theme === 'light' ? '#888' : '#808080'};
  font-size: 0.85rem;
  white-space: nowrap;
  font-family: 'Monaco', 'Consolas', monospace;
`;

const Location = styled.span`
  color: ${props => props.theme === 'light' ? '#999' : '#707070'};
  font-size: 0.85rem;
  font-style: italic;
`;

const AchievementList = styled.ul`
  list-style: none;
  padding: 0;
  margin: 12px 0 0 0;
`;

const EntryNote = styled.p`
  color: ${props => props.theme === 'light' ? '#999' : '#888'};
  font-style: italic;
  font-size: 0.9rem;
  margin: 10px 0 0 0;
`;

const AchievementItem = styled.li`
  color: ${props => props.theme === 'light' ? '#555' : '#b0b0b0'};
  line-height: 1.6;
  margin-bottom: 8px;
  padding-left: 20px;
  position: relative;
  font-size: 0.92rem;

  &:before {
    content: "▸";
    color: var(--highlight-color-${props => props.theme === 'light' ? 'light' : 'dark'});
    font-weight: bold;
    position: absolute;
    left: 0;
    font-size: 0.85rem;
  }

  strong {
    color: var(--highlight-color-${props => props.theme === 'light' ? 'light' : 'dark'});
  }

  &:last-child {
    margin-bottom: 0;
  }
`;

const entryVariants = {
  hidden: { opacity: 0, x: -20 },
  visible: (i) => ({
    opacity: 1,
    x: 0,
    transition: {
      delay: i * 0.12,
      duration: 0.4,
      type: 'spring',
      stiffness: 260,
      damping: 24,
    },
  }),
};

const experiences = [
  {
    position: 'Senior Product Manager',
    company: 'OpenUp',
    period: 'Jan 2026 – Present',
    location: 'Amsterdam, Netherlands',
    current: true,
    achievements: [],
  },
  {
    position: 'Product Manager',
    company: 'Just Eat Takeaway - Grocery & Retail',
    period: 'Jul 2024 – Dec 2025',
    location: 'Amsterdam, Netherlands',
    achievements: [
      <>Defined and operationalized <strong>North Star Metric</strong> for Catalogue Information, directly aligning team efforts with company KPIs and driving measurable business impact</>,
      <>Led successful launch of databank integrations across <strong>10+ markets</strong>, significantly improving image coverage (<strong>85% YoY increase</strong>) and product description coverage (<strong>45% YoY</strong>), resulting in enhanced customer experience and partner performance</>,
      <>Established strategic product vision for <strong>Product Detail Pages (PDPs)</strong>, creating phased delivery approach that enabled iterative value delivery while maintaining long-term alignment</>,
    ],
  },
  {
    position: 'Product Manager',
    company: 'Just Eat Takeaway - Partner Catalogue',
    period: 'Feb 2023 – Jun 2024',
    location: 'Amsterdam, Netherlands',
    achievements: [
      <>Enabled autonomous partner capabilities by implementing self-service tools that directly contributed to increased partner satisfaction, reduced operational overhead and generated a <strong>€150 million</strong> GMV uplift in 1 year.</>,
      <>Delivered self-service and automation capabilities for Just Eat Partners and Customer Service, resulting in an increase in CS automation rate from <strong>64.75% to 95.7%</strong>, translating to <strong>108 FTE savings</strong> in customer service agents.</>,
      <>Developed and delivered a <strong>user-centered product strategy</strong> grounded in field research and stakeholder insights, resulting in streamlined menu management processes and a boost in user satisfaction.</>,
    ],
  },
  {
    position: 'Associate Product Manager',
    company: 'Just Eat Takeaway - Partner Access',
    period: 'Feb 2022 – Jan 2023',
    location: 'Amsterdam, Netherlands',
    achievements: [
      <>Led integration of <strong>Keycloak as Single Sign-On (SSO)</strong> solution across Partner Hub, unifying authentication for all partner-facing products and significantly enhancing security and user autonomy</>,
      <>Designed and implemented intuitive authentication and password reset flows, resulting in <strong>30% decrease</strong> in customer support tickets</>,
      <>Enabled autonomous partner account creation, reducing onboarding drop-off rates and improving partner satisfaction during critical onboarding phase</>,
      <>Spearheaded redesign and launch of <strong>Partner Hub landing page MVP</strong>, establishing foundation for unified global partner ecosystem and improving user experience across multiple markets</>,
    ],
  },
];

const ExperienceSection = () => {
  const { theme } = useContext(ThemeContext);

  return (
    <Container>
      <Title theme={theme}>Professional <span className="highlight">Experience</span></Title>

      <EntryList>
        {experiences.map((exp, i) => (
          <motion.div
            key={i}
            custom={i}
            initial="hidden"
            animate="visible"
            variants={entryVariants}
          >
            <Entry theme={theme}>
              <EntryHeader>
                <div>
                  <Position theme={theme}>
                    {exp.position}
                    {exp.current && <CurrentBadge theme={theme}>Current</CurrentBadge>}
                  </Position>
                  <Company theme={theme}>{exp.company}</Company>
                </div>
                <div style={{ textAlign: 'right' }}>
                  <Period theme={theme}>{exp.period}</Period>
                  <br />
                  <Location theme={theme}>{exp.location}</Location>
                </div>
              </EntryHeader>
              {exp.achievements.length > 0 ? (
                <AchievementList>
                  {exp.achievements.map((achievement, j) => (
                    <AchievementItem key={j} theme={theme}>
                      {achievement}
                    </AchievementItem>
                  ))}
                </AchievementList>
              ) : (
                <EntryNote theme={theme}>Recently started — highlights coming soon.</EntryNote>
              )}
            </Entry>
          </motion.div>
        ))}
      </EntryList>
    </Container>
  );
};

export default ExperienceSection; 